import React from 'react';
import { useI18n } from './i18n';
import ScoreSlider from './ScoreSlider';
import { RangeSlider } from './PairingForm';

// Complete dish tasting sheet, AIS/Mercadini structure (docs/schema-v2.md §5.0c): the three phase blocks, the
// qualifiers of three Mercadini sensations and the band labels. Marks are optional integers 1-10 (null = not scored).

export const FOOD_PHASES = [
  { phase: 'visual', fields: ['visual_appearance', 'visual_presentation'] },
  { phase: 'olfactory', fields: ['olfactory_frankness', 'olfactory_intensity', 'olfactory_quality', 'olfactory_harmony'] },
  { phase: 'final', fields: ['final_balance', 'final_harmony'] },
];
export const FOOD_MARK_FIELDS = FOOD_PHASES.flatMap((p) => p.fields);

// qualifier field -> allowed codes; each one sits next to the sensation it qualifies
export const FOOD_QUALIFIERS = {
  succulence_type: ['intrinsic', 'induced', 'added'],
  bitterness_origin: ['intrinsic', 'cooking'],
  spiciness_type: ['pungent', 'sweet'],
};
const QUALIFIER_AFTER = { food_succulence: 'succulence_type', food_bitter_tendency: 'bitterness_origin', food_spiciness: 'spiciness_type' };

// AIS bands. Phase marks: 1-4 insufficient, 4-7 acceptable, 7-10 inviting. Sensations 0-10: 0-2 imperceptible,
// 2-4 slightly, 4-6 fairly, 6-8 perceptible, 8-10 very perceptible. Structure: 1-4 little, 4-7 fairly, 7-10 structured.
// A value that sits on a boundary belongs to the upper band; the labels are display only and never stored.
export function phaseBand(value) {
  if (value === null || value === undefined || value === '') return null;
  const v = Number(value);
  return v < 4 ? 'insufficient' : (v < 7 ? 'acceptable' : 'inviting');
}

export function sensationBand(key, value) {
  if (value === null || value === undefined || value === '') return null;       // not filled in: no band
  const v = Number(value);
  if (key === 'food_structure') return v < 4 ? 'structure.little' : (v < 7 ? 'structure.fairly' : 'structure.structured');
  if (v < 2) return 'sensation.imperceptible';
  if (v < 4) return 'sensation.slightly';
  if (v < 6) return 'sensation.fairly';
  if (v < 8) return 'sensation.perceptible';
  return 'sensation.very';
}

const present = (value) => value !== null && value !== undefined && value !== '';

// One of the 12 Mercadini sensations: the existing slider plus its band label (value and payload key unchanged)
// and, for succulence, bitter tendency and spiciness, the qualifier select.
export function FoodSensation({ axisKey, form, onChange }) {
  const { t } = useI18n();
  const qualifier = QUALIFIER_AFTER[axisKey];
  return (
    <div className="food-sensation">
      <RangeSlider name={axisKey} value={form[axisKey]} onChange={onChange} side="food" />
      {sensationBand(axisKey, form[axisKey]) && <small className="band-label">{t(`food.band.${sensationBand(axisKey, form[axisKey])}`)}</small>}
      {qualifier && (
        <div className="form-field food-qualifier">
          <label>{t(`food.q.${qualifier}`)}</label>
          <select value={form[qualifier] || ''} onChange={(e) => onChange(qualifier, e.target.value)}>
            <option value="">{t('food.q.none')}</option>
            {FOOD_QUALIFIERS[qualifier].map((code) => <option key={code} value={code}>{t(`food.q.${qualifier}.${code}`)}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

// A phase block (visual / olfactory / final): optional 1-10 marks with the AIS band of the current value
export function FoodPhase({ phase, fields, form, onChange }) {
  const { t } = useI18n();
  return (
    <fieldset>
      <legend className="legend-food">{t(`food.phase.${phase}`)}</legend>
      {fields.map((field) => {
        const band = phaseBand(form[field]);
        return (
          <div key={field} className="food-mark">
            <ScoreSlider label={t(`food.m.${field}`)} value={form[field]} min={1} max={10} step={1} unit="/10"
              onChange={(v) => onChange(field, v)} />
            {band && <small className="band-label">{t(`food.band.phase.${band}`)}</small>}
          </div>
        );
      })}
    </fieldset>
  );
}

// Read-only summary of the extra fields of a food sheet, for the lists and details (anything missing is left out)
export function FoodSheetExtras({ sheet }) {
  const { t, formatNumber } = useI18n();
  const means = FOOD_PHASES.filter(({ phase }) => present(sheet[`${phase}_mean`]));
  const qualifiers = Object.keys(FOOD_QUALIFIERS).filter((field) => present(sheet[field]));
  if (!sheet.preparation && means.length === 0 && qualifiers.length === 0) return null;
  return (
    <div className="food-extras">
      {sheet.preparation && <div className="food-extra-line"><strong>{t('food.preparation')}:</strong> {sheet.preparation}</div>}
      {qualifiers.length > 0 && (
        <div className="food-extra-line">
          {qualifiers.map((field) => (
            <span key={field} className="chip food-qualifier-chip">{t(`food.q.${field}`)}: {t(`food.q.${field}.${sheet[field]}`)}</span>
          ))}
        </div>
      )}
      {means.length > 0 && (
        <div className="food-extra-line">
          <strong>{t('food.means_title')}:</strong>{' '}
          {means.map(({ phase }) => `${t(`food.means.${phase}`)} ${formatNumber(sheet[`${phase}_mean`], 1)}`).join(' · ')}
        </div>
      )}
    </div>
  );
}
